import {
  type Transform,
  type ViewerModelApi,
  type ViewerModelHandle,
  type GizmoMode,
} from '@mindcloudxai/shared';
import {
  Entity,
  Asset,
  Vec3 as PcVec3,
  Quat as PcQuat,
  Gizmo,
  TranslateGizmo,
  RotateGizmo,
  ScaleGizmo,
  type ContainerResource,
  type AppBase,
  type Layer,
} from 'playcanvas';
import type { ViewerContext } from '../types';

interface ModelEntry {
  entity: Entity;
  asset: Asset | null;
  name: string;
  /** 记录原始导入来源，用于复制功能重新导入 */
  source: { url?: string; file?: File };
}

export interface ModelApiWithCleanup extends ViewerModelApi {
  /** 场景切换/释放时清理所有已导入模型 */
  _clearAll(): void;
}

export function createModelApi(
  getCtx: () => ViewerContext,
  assertInitialized: () => void,
): ModelApiWithCleanup {
  // 模型实体挂载在 app.root 下，与 splat 场景共存
  const models = new Map<string, ModelEntry>();
  let nextModelId = 0;

  // gizmo 管理：同一时间只激活一个 gizmo，绑定到一个模型 entity
  let activeGizmo: TranslateGizmo | RotateGizmo | ScaleGizmo | null = null;
  let activeGizmoMode: GizmoMode | null = null;
  let activeGizmoModelId: string | null = null;
  let gizmoLayer: Layer | null = null;
  // pivot 放在模型 AABB 中心，gizmo 绑定到 pivot 使控制点出现在模型中点
  let gizmoPivot: Entity | null = null;

  function emitChanged() {
    const ctx = getCtx();
    const handles: ViewerModelHandle[] = [];
    models.forEach((entry, id) => {
      handles.push({ id, name: entry.name, transform: readTransform(entry.entity) });
    });
    ctx.events.fire('model:changed', handles);
    ctx.app.renderNextFrame = true;
  }

  function emitTransform(modelId: string, transform: Transform) {
    const ctx = getCtx();
    ctx.events.fire('model:transform', { modelId, transform });
    ctx.app.renderNextFrame = true;
  }

  function destroyGizmo() {
    if (activeGizmo) {
      activeGizmo.detach();
      activeGizmo.destroy();
      activeGizmo = null;
    }
    if (gizmoPivot) {
      gizmoPivot.destroy();
      gizmoPivot = null;
    }
    activeGizmoMode = null;
    activeGizmoModelId = null;
  }

  function _clearAll(): void {
    const ctx = getCtx();
    destroyGizmo();
    // 场景切换/dispose 时重置 NavInteraction 状态，避免 asset panel 打开期间
    // 重载场景导致 model.editMode/gizmoBusy 残留为 true 永久锁死点击导航
    ctx.global?.events.fire('model.editMode', false);
    ctx.global?.events.fire('model.gizmoBusy', false);
    models.forEach((entry) => {
      entry.entity.destroy();
      if (entry.asset) {
        ctx.app.assets.remove(entry.asset);
      }
    });
    models.clear();
    emitChanged();
  }

  /** 计算模型所有 meshInstance 的合并世界 AABB（min/max/center/halfExtents） */
  function getModelWorldBounds(entity: Entity): {
    min: PcVec3;
    max: PcVec3;
    center: PcVec3;
    halfExtents: PcVec3;
  } | null {
    let min: PcVec3 | null = null;
    let max: PcVec3 | null = null;

    const visit = (node: Entity) => {
      const render = node.render;
      if (!render?.meshInstances?.length) return;
      for (const mi of render.meshInstances) {
        // mi.aabb 在渲染时更新为世界空间 AABB，但首帧前可能未更新。
        // 若 AABB 无效（体积 ≤ 0），用 localAabb + worldTransform 手动计算。
        const aabb = mi.aabb;
        const aMin = aabb.getMin();
        const aMax = aabb.getMax();
        const isStale = aMax.x - aMin.x <= 0 && aMax.y - aMin.y <= 0 && aMax.z - aMin.z <= 0;
        if (isStale && mi.mesh?.aabb) {
          // 回退：用 mesh local AABB 经 worldTransform 变换到世界空间
          const localMin = mi.mesh.aabb.getMin();
          const localMax = mi.mesh.aabb.getMax();
          const wt = node.getWorldTransform();
          const corners = [
            new PcVec3(localMin.x, localMin.y, localMin.z),
            new PcVec3(localMax.x, localMin.y, localMin.z),
            new PcVec3(localMin.x, localMax.y, localMin.z),
            new PcVec3(localMax.x, localMax.y, localMin.z),
            new PcVec3(localMin.x, localMin.y, localMax.z),
            new PcVec3(localMax.x, localMin.y, localMax.z),
            new PcVec3(localMin.x, localMax.y, localMax.z),
            new PcVec3(localMax.x, localMax.y, localMax.z),
          ];
          for (const c of corners) {
            wt.transformPoint(c, c);
            if (!min || !max) {
              min = new PcVec3(c.x, c.y, c.z);
              max = new PcVec3(c.x, c.y, c.z);
            } else {
              min.x = Math.min(min.x, c.x);
              min.y = Math.min(min.y, c.y);
              min.z = Math.min(min.z, c.z);
              max.x = Math.max(max.x, c.x);
              max.y = Math.max(max.y, c.y);
              max.z = Math.max(max.z, c.z);
            }
          }
          continue;
        }
        if (!min || !max) {
          min = new PcVec3(aMin.x, aMin.y, aMin.z);
          max = new PcVec3(aMax.x, aMax.y, aMax.z);
        } else {
          min.x = Math.min(min.x, aMin.x);
          min.y = Math.min(min.y, aMin.y);
          min.z = Math.min(min.z, aMin.z);
          max.x = Math.max(max.x, aMax.x);
          max.y = Math.max(max.y, aMax.y);
          max.z = Math.max(max.z, aMax.z);
        }
      }
    };

    visit(entity);
    entity.forEach((node) => visit(node as Entity));

    if (!min || !max) return null;
    const mn = min as PcVec3;
    const mx = max as PcVec3;
    const center = new PcVec3((mn.x + mx.x) / 2, (mn.y + mx.y) / 2, (mn.z + mx.z) / 2);
    const halfExtents = new PcVec3((mx.x - mn.x) / 2, (mx.y - mn.y) / 2, (mx.z - mn.z) / 2);
    return { min: mn, max: mx, center, halfExtents };
  }

  /** 计算模型世界 AABB 中心，用于 gizmo 定位 */
  function getModelWorldCenter(entity: Entity): PcVec3 {
    const bounds = getModelWorldBounds(entity);
    return bounds ? bounds.center : entity.getPosition();
  }

  /** 创建或切换 gizmo，绑定到指定模型 entity */
  function setGizmo(modelId: string, mode: GizmoMode | null): void {
    const ctx = getCtx();

    // mode 为 null → 移除 gizmo（无论 modelId 是否有效）
    if (!mode) {
      destroyGizmo();
      ctx.app.renderNextFrame = true;
      return;
    }

    const entry = models.get(modelId);
    if (!entry) return;

    // 同一模型 + 同一模式 → 无需切换
    if (activeGizmo && activeGizmoMode === mode && activeGizmoModelId === modelId) return;

    // 销毁旧 gizmo
    destroyGizmo();

    const cameraComp = ctx.camera.camera;
    if (!cameraComp) return;

    // 确保 gizmo 渲染层存在
    const existing = ctx.app.scene.layers.getLayerByName('Gizmo');
    gizmoLayer = existing ?? Gizmo.createLayer(ctx.app as AppBase);
    if (!cameraComp.layers.includes(gizmoLayer.id)) {
      cameraComp.layers = [...cameraComp.layers, gizmoLayer.id];
    }

    // 在模型 AABB 中心创建 pivot，gizmo 控制点出现在模型中点
    const center = getModelWorldCenter(entry.entity);
    gizmoPivot = new Entity('modelGizmoPivot');
    ctx.app.root.addChild(gizmoPivot);
    gizmoPivot.setPosition(center);
    // 同步 pivot 的 rotation/scale 与 model entity 一致
    gizmoPivot.setLocalRotation(entry.entity.getLocalRotation());
    gizmoPivot.setLocalScale(entry.entity.getLocalScale());

    // 根据模式创建对应 gizmo
    if (mode === 'translate') {
      activeGizmo = new TranslateGizmo(cameraComp, gizmoLayer);
    } else if (mode === 'rotate') {
      activeGizmo = new RotateGizmo(cameraComp, gizmoLayer);
    } else {
      activeGizmo = new ScaleGizmo(cameraComp, gizmoLayer);
    }

    activeGizmoMode = mode;
    activeGizmoModelId = modelId;

    // 调整 gizmo 大小
    updateGizmoSize(ctx.canvas);

    // 拖拽前的初始状态，用于计算 delta 同步到 model entity
    let dragStartPivotPos: PcVec3 | null = null;
    let dragStartModelPos: PcVec3 | null = null;
    // scale/rotate 专用：记录初始 scale、rotation 和 model→center 偏移量
    let dragStartScale: PcVec3 | null = null;
    let dragStartRot: PcQuat | null = null;
    let dragStartOffset: PcVec3 | null = null; // center - modelOrigin

    // gizmo 事件：拖拽时同步 transform 到 entity 并通知上层
    activeGizmo.on('render:update', () => {
      ctx.app.renderNextFrame = true;
    });
    activeGizmo.on('transform:start', () => {
      // 通知 InputController 在 gizmo 拖拽期间丢弃相机输入
      // 必须用 global.events（supersplat-viewer 内部事件），
      // InputController/NavInteraction 监听的是 global.events 而非 ctx.events
      ctx.global?.events.fire('model.gizmoBusy', true);
      if (gizmoPivot) {
        dragStartPivotPos = gizmoPivot.getLocalPosition().clone();
      }
      dragStartModelPos = entry.entity.getLocalPosition().clone();
      dragStartScale = entry.entity.getLocalScale().clone();
      dragStartRot = entry.entity.getLocalRotation().clone();
      // center - modelOrigin：缩放/旋转时用此偏移保持中心不动
      if (dragStartPivotPos && dragStartModelPos) {
        dragStartOffset = new PcVec3(
          dragStartPivotPos.x - dragStartModelPos.x,
          dragStartPivotPos.y - dragStartModelPos.y,
          dragStartPivotPos.z - dragStartModelPos.z,
        );
      }
    });
    activeGizmo.on('transform:move', () => {
      if (!gizmoPivot || !dragStartPivotPos || !dragStartModelPos) return;
      if (mode === 'translate') {
        // translate: 把 pivot 位移 delta 同步到 model entity
        const pivotPos = gizmoPivot.getLocalPosition();
        entry.entity.setLocalPosition(
          dragStartModelPos.x + (pivotPos.x - dragStartPivotPos.x),
          dragStartModelPos.y + (pivotPos.y - dragStartPivotPos.y),
          dragStartModelPos.z + (pivotPos.z - dragStartPivotPos.z),
        );
      } else if (mode === 'scale' && dragStartScale && dragStartOffset) {
        // scale: 以 gizmo 位置（模型中心）为锚点缩放，
        // 偏移量随 scale 变化，保持中心不动
        const newScale = gizmoPivot.getLocalScale();
        const sX = dragStartScale.x !== 0 ? newScale.x / dragStartScale.x : 1;
        const sY = dragStartScale.y !== 0 ? newScale.y / dragStartScale.y : 1;
        const sZ = dragStartScale.z !== 0 ? newScale.z / dragStartScale.z : 1;
        entry.entity.setLocalPosition(
          dragStartPivotPos.x - dragStartOffset.x * sX,
          dragStartPivotPos.y - dragStartOffset.y * sY,
          dragStartPivotPos.z - dragStartOffset.z * sZ,
        );
        entry.entity.setLocalScale(newScale.x, newScale.y, newScale.z);
      } else if (mode === 'rotate' && dragStartRot && dragStartOffset) {
        // rotate: 以 gizmo 位置为圆心旋转，
        // 用 delta rotation 旋转 model→center 偏移，保持中心不动
        const newRot = gizmoPivot.getLocalRotation();
        // deltaRot = newRot * inverse(startRot)
        const deltaRot = new PcQuat().copy(newRot).mul(new PcQuat().copy(dragStartRot).invert());
        const rotatedOffset = new PcVec3();
        deltaRot.transformVector(dragStartOffset, rotatedOffset);
        // model 新位置 = center - rotatedOffset
        entry.entity.setLocalPosition(
          dragStartPivotPos.x - rotatedOffset.x,
          dragStartPivotPos.y - rotatedOffset.y,
          dragStartPivotPos.z - rotatedOffset.z,
        );
        entry.entity.setLocalRotation(newRot);
      }
      emitTransform(modelId, readTransform(entry.entity));
    });
    activeGizmo.on('transform:end', () => {
      ctx.global?.events.fire('model.gizmoBusy', false);
      dragStartPivotPos = null;
      dragStartModelPos = null;
      dragStartScale = null;
      dragStartRot = null;
      dragStartOffset = null;
      emitTransform(modelId, readTransform(entry.entity));
    });

    // gizmo 绑定到 pivot（而非 model entity），使控制点位于模型中点
    activeGizmo.attach(gizmoPivot);
    ctx.app.renderNextFrame = true;
  }

  function updateGizmoSize(canvas: HTMLCanvasElement) {
    if (!activeGizmo) return;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const m = Math.max(w, h);
    if (m > 0) {
      const isCoarse =
        typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
      activeGizmo.size = (isCoarse ? 1000 : 1200) / m;
    }
  }

  // 提取为命名函数，使 duplicateModel 能直接调用
  async function addModel(options: {
    url?: string;
    file?: File;
    name?: string;
  }): Promise<ViewerModelHandle> {
    assertInitialized();
    const ctx = getCtx();
    const { url, file, name } = options;

    const modelUrl = file ? URL.createObjectURL(file) : url;
    if (!modelUrl) throw new Error('addModel: url or file is required');

    const modelId = `model-${++nextModelId}`;
    const displayName = name ?? file?.name ?? 'Imported Model';

    // PlayCanvas container asset 加载 GLB/GLTF
    let filename: string;
    if (file?.name) {
      filename = file.name;
    } else {
      try {
        filename = new URL(modelUrl, location.href).pathname.split('/').pop() || 'model.glb';
      } catch {
        filename = 'model.glb';
      }
    }
    const asset = new Asset(modelId, 'container', { url: modelUrl, filename });
    ctx.app.assets.add(asset);

    try {
      await new Promise<void>((resolve, reject) => {
        asset.on('load', () => resolve());
        asset.on('error', (err: unknown) => reject(new Error(String(err))));
        ctx.app.assets.load(asset);
      });
    } catch (err) {
      // 加载失败：清理 asset 和 blob URL，避免泄漏
      ctx.app.assets.remove(asset);
      if (file) URL.revokeObjectURL(modelUrl);
      throw err;
    }

    if (ctx.disposed) {
      // 加载期间被 dispose，清理已加载资源
      ctx.app.assets.remove(asset);
      if (file) URL.revokeObjectURL(modelUrl);
      throw new Error('Viewer disposed during model load');
    }

    // 从 container asset 实例化可渲染实体
    const resource = asset.resource as ContainerResource | undefined;
    const entity =
      resource?.instantiateRenderEntity({
        castShadows: true,
        receiveShadows: true,
      }) ?? new Entity(modelId);

    // 放置在相机前方 ~3 单位
    const camPos = ctx.camera.getPosition();
    const camFwd = ctx.camera.forward.clone().normalize();
    const placePos = new PcVec3(
      camPos.x + camFwd.x * 3,
      camPos.y + camFwd.y * 3,
      camPos.z + camFwd.z * 3,
    );
    entity.setLocalPosition(placePos);

    ctx.app.root.addChild(entity);
    models.set(modelId, { entity, asset, name: displayName, source: { url, file } });

    if (file) URL.revokeObjectURL(modelUrl);

    const transform = readTransform(entity);
    const handle: ViewerModelHandle = { id: modelId, name: displayName, transform };
    emitChanged();
    return handle;
  }

  return {
    addModel,

    duplicateModel: async (modelId) => {
      const entry = models.get(modelId);
      if (!entry) throw new Error(`duplicateModel: model ${modelId} not found`);
      const { source, name } = entry;
      // 重新导入相同资源，复制时在名称后加 (copy)
      const handle = await addModel({ url: source.url, file: source.file, name: `${name} (copy)` });
      // position 偏移 1 单位，其余 transform 保持一致
      const origTransform = readTransform(entry.entity);
      const origPos = origTransform.position ?? { x: 0, y: 0, z: 0 };
      const offsetTransform: Transform = {
        position: {
          x: origPos.x + 1,
          y: origPos.y,
          z: origPos.z,
        },
        rotation: origTransform.rotation,
        scale: origTransform.scale,
      };
      const newEntry = models.get(handle.id);
      if (newEntry) {
        applyTransform(newEntry.entity, offsetTransform);
        emitTransform(handle.id, offsetTransform);
      }
      return { ...handle, transform: offsetTransform };
    },

    removeModel: (modelId) => {
      const entry = models.get(modelId);
      if (!entry) return;
      // 如果正在对该模型使用 gizmo，先销毁
      if (activeGizmoModelId === modelId) {
        destroyGizmo();
      }
      entry.entity.destroy();
      if (entry.asset) {
        getCtx().app.assets.remove(entry.asset);
      }
      models.delete(modelId);
      emitChanged();
    },

    setModelTransform: (modelId, transform) => {
      const entry = models.get(modelId);
      if (!entry) return;
      applyTransform(entry.entity, transform);
      emitTransform(modelId, readTransform(entry.entity));
    },

    getModelTransform: (modelId) => {
      const entry = models.get(modelId);
      if (!entry) return null;
      return readTransform(entry.entity);
    },

    getModels: () => {
      const handles: ViewerModelHandle[] = [];
      models.forEach((entry, id) => {
        handles.push({ id, name: entry.name, transform: readTransform(entry.entity) });
      });
      return handles;
    },

    pickModel: async (options) => {
      const ctx = getCtx();
      if (ctx.disposed || models.size === 0) return null;

      const camComp = ctx.camera.camera;
      if (!camComp) return null;

      const rect = ctx.canvas.getBoundingClientRect();
      // screenToWorld 需要像素坐标（相对 canvas），不是归一化坐标
      const px = options.clientX - rect.left;
      const py = options.clientY - rect.top;
      if (px < 0 || px > rect.width || py < 0 || py > rect.height) return null;

      // screenToWorld 返回射线起点和方向，用 bounding box 距离判定命中
      const worldPos = new PcVec3();
      camComp.screenToWorld(px, py, 1.0, worldPos);

      const rayOrigin = ctx.camera.getPosition();
      const rayDir = new PcVec3(
        worldPos.x - rayOrigin.x,
        worldPos.y - rayOrigin.y,
        worldPos.z - rayOrigin.z,
      ).normalize();

      let closestId: string | null = null;
      let closestDist = Infinity;

      models.forEach((entry, id) => {
        // 使用世界空间 AABB 做射线相交
        const bounds = getModelWorldBounds(entry.entity);
        if (!bounds) return;
        const hit = rayAabbIntersect(rayOrigin, rayDir, bounds.min, bounds.max);
        if (hit !== null && hit < closestDist) {
          closestDist = hit;
          closestId = id;
        }
      });

      return closestId ? { modelId: closestId } : null;
    },

    setGizmo,

    focusModel: (modelId: string) => {
      const ctx = getCtx();
      const entry = models.get(modelId);
      if (!entry) return;

      const bounds = getModelWorldBounds(entry.entity);
      if (!bounds) return;

      const camComp = ctx.camera.camera;
      if (!camComp) return;

      // 当前相机方向（position → target），保持方向不变只调距离
      const camPos = ctx.camera.getPosition();
      const camTarget = new PcVec3();
      if (ctx.viewer?.cameraManager) {
        ctx.viewer.cameraManager.camera.calcFocusPoint(camTarget);
      } else {
        camTarget.copy(camPos).add(ctx.camera.forward);
      }
      const dir = new PcVec3(
        camPos.x - camTarget.x,
        camPos.y - camTarget.y,
        camPos.z - camTarget.z,
      );
      const dirLen = dir.length();
      if (dirLen < 1e-6) return;
      dir.mulScalar(1 / dirLen);

      // 根据 bounding sphere 半径和 FOV 计算所需距离
      // 使用包围球半径（AABB 中心到顶点的距离）确保任意角度都能看到完整 bounding box
      const halfDiag = new PcVec3(bounds.halfExtents.x, bounds.halfExtents.y, bounds.halfExtents.z);
      const radius = halfDiag.length();
      const fov = ((camComp.fov ?? 60) * Math.PI) / 180;
      // 留 50% 边距
      const distance = (radius / Math.sin(fov / 2)) * 1.5;

      const newPos = new PcVec3(
        bounds.center.x + dir.x * distance,
        bounds.center.y + dir.y * distance,
        bounds.center.z + dir.z * distance,
      );

      // 使用 cameraManager 的 look + snap 实现即时定位
      if (ctx.viewer?.cameraManager) {
        const managedCamera = ctx.viewer.cameraManager.camera;
        managedCamera.look(newPos, bounds.center);
        ctx.viewer.cameraManager.snap();
      } else {
        ctx.camera.setPosition(newPos.x, newPos.y, newPos.z);
        ctx.camera.lookAt(bounds.center);
      }
      ctx.app.renderNextFrame = true;
    },

    _clearAll,

    setEditMode: (active: boolean) => {
      const ctx = getCtx();
      ctx.global?.events.fire('model.editMode', active);
    },
  };
}

// ---------------------------------------------------------------------------
// 辅助函数
// ---------------------------------------------------------------------------

function readTransform(entity: Entity): Transform {
  const pos = entity.getLocalPosition();
  const rot = entity.getLocalRotation();
  const scale = entity.getLocalScale();
  // PlayCanvas 用四元数，转换为 Euler 弧度
  const e = new PcQuat(rot.x, rot.y, rot.z, rot.w).getEulerAngles();
  // getEulerAngles 返回角度，转换为弧度
  return {
    position: { x: pos.x, y: pos.y, z: pos.z },
    rotation: {
      x: (e.x * Math.PI) / 180,
      y: (e.y * Math.PI) / 180,
      z: (e.z * Math.PI) / 180,
    },
    scale: { x: scale.x, y: scale.y, z: scale.z },
  };
}

function applyTransform(entity: Entity, transform: Transform): void {
  if (transform.position) {
    entity.setLocalPosition(transform.position.x, transform.position.y, transform.position.z);
  }
  if (transform.rotation) {
    const q = new PcQuat().setFromEulerAngles(
      (transform.rotation.x * 180) / Math.PI,
      (transform.rotation.y * 180) / Math.PI,
      (transform.rotation.z * 180) / Math.PI,
    );
    entity.setLocalRotation(q);
  }
  if (transform.scale) {
    if (typeof transform.scale === 'number') {
      entity.setLocalScale(transform.scale, transform.scale, transform.scale);
    } else {
      entity.setLocalScale(transform.scale.x, transform.scale.y, transform.scale.z);
    }
  }
}

/** 射线与 AABB 相交检测，返回最近命中距离或 null */
function rayAabbIntersect(origin: PcVec3, dir: PcVec3, min: PcVec3, max: PcVec3): number | null {
  let tmin = -Infinity;
  let tmax = Infinity;

  for (const axis of ['x', 'y', 'z'] as const) {
    const o = origin[axis];
    const d = dir[axis];
    const mn = min[axis];
    const mx = max[axis];

    if (Math.abs(d) < 1e-8) {
      if (o < mn || o > mx) return null;
    } else {
      const t1 = (mn - o) / d;
      const t2 = (mx - o) / d;
      const tnear = Math.min(t1, t2);
      const tfar = Math.max(t1, t2);
      if (tnear > tmin) tmin = tnear;
      if (tfar < tmax) tmax = tfar;
      if (tmin > tmax) return null;
    }
  }

  return tmin >= 0 ? tmin : tmax >= 0 ? 0 : null;
}
